import React, { useCallback, useContext, useMemo } from "react";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import type { Placement } from "tippy.js";
import uuidv4 from "uuid/v4";

import { PaywallContent2034Q4 } from "~/billing/PaywallContent2023Q4";
import { Button } from "~/ui";
import { DEFAULT_TABS } from "~/ui/Charts/constants";
import Tooltip from "~/ui/Tooltip";
import { AnalyticsContext } from "~/workspaceAnalytics/types";

import styles from "./ChartWrapper.module.css";

export interface ChartWrapperProps {
	title: string;
	subtitle?: string;
	label?: string;
	infoContent?: JSX.Element | string;
	infoPlacement?: Placement;
	tabs?: Array<string>;
	activeTab?: string;
	onSelectTab?(selectedTab: string): void;
	className?: string;
	paywallApplicable?: boolean;
}

export interface TabProps {
	tab: string;
	activeTab?: string;
	onSelectTab(selectedTab: string): void;
}

function Tab({ tab, activeTab, onSelectTab }: TabProps) {
	const onClick = useCallback(() => {
		onSelectTab(tab);
	}, [tab, onSelectTab]);

	return (
		<Button size="wide" onClick={onClick} className={tab === activeTab ? styles.activeTab : ""}>
			{tab}
		</Button>
	);
}

export default function ChartWrapper({
	title,
	subtitle,
	label,
	infoContent,
	infoPlacement = "top",
	tabs: propsTabs,
	activeTab,
	onSelectTab,
	className,
	paywallApplicable,
	children,
}: React.PropsWithChildren<ChartWrapperProps>) {
	const { hasAccessToAnalytics } = useContext(AnalyticsContext);
	const tabs = useMemo(() => {
		return propsTabs || DEFAULT_TABS;
	}, [propsTabs]);
	const analyticsProperties = useMemo(() => {
		return {
			fingerprint: uuidv4(),
			ux: "banner",
			feature: "analytics_v2",
		};
	}, []);
	return (
		<article className={cx(styles.wrapper, className)}>
			{paywallApplicable && !hasAccessToAnalytics && (
				<div className={styles.paywallContainer}>
					<PaywallContent2034Q4 featureKey="analytics" analyticsProperties={analyticsProperties} />
				</div>
			)}
			<header className={styles.header}>
				<div className={styles.titleWrapper}>
					<h1>
						{title}
						{infoContent && (
							<Tooltip placement={infoPlacement} content={infoContent}>
								<span>
									<FontAwesomeIcon icon={faInfoCircle} className={styles.tooltip} />
								</span>
							</Tooltip>
						)}
					</h1>
					{label && <span className={styles.label}>{label}</span>}
				</div>
				{subtitle && <h2>{subtitle}</h2>}
			</header>
			<div className={styles.chartContainer}>
				{activeTab && onSelectTab && (
					<nav className={styles.nav}>
						{tabs.map(tab => (
							<Tab key={tab} tab={tab} activeTab={activeTab} onSelectTab={onSelectTab} />
						))}
					</nav>
				)}
				{children}
			</div>
		</article>
	);
}
